export type DashboardReferenceKind =
  | "component"
  | "chart"
  | "analysis"
  | "folder"
  | "feature"
  | "action"
  | "dataset"
  | "report"

export type DashboardReference = {
  id: string
  label: string
  kind: DashboardReferenceKind
  description?: string
  path?: string
  importance?: "low" | "medium" | "high" | "critical"
  whenToUse?: string
  inputs?: string[]
  outputs?: string[]
  chartTypes?: string[]
}

export type DashboardComponentIndex = {
  version?: string
  summary?: string
  references?: DashboardReference[]
}

export const chartTypes = ["line", "bar", "doughnut", "pie", "radar", "polarArea"] as const

export const dashboardComponentIndex: DashboardComponentIndex = {
  version: "openanalyst-dashboard-components/v1",
  summary:
    "Use this index to choose dashboard blocks, chart types, action surfaces, and project references for plugin-owned dashboards.",
  references: [
    {
      id: "block.stats",
      label: "Stats",
      kind: "component",
      importance: "critical",
      description: "Compact KPI tiles for revenue, conversion, counts, health, and trend summaries.",
      whenToUse: "Use at the top of dashboards when the user needs quick status or before/after comparisons.",
      outputs: ["KPI label", "current value", "trend direction", "change summary"],
    },
    {
      id: "block.chart",
      label: "Chart",
      kind: "component",
      importance: "critical",
      description: "Time series, categorical comparisons, composition, and radial score views.",
      whenToUse: "Use when values need shape, comparison, share, or momentum instead of only a table.",
      chartTypes: [...chartTypes],
      outputs: ["Chart labels", "one or more numeric series", "optional colors", "chart options"],
    },
    {
      id: "chart.line",
      label: "Line Chart",
      kind: "chart",
      importance: "high",
      description: "Best for time series, velocity, cumulative progress, and trend direction.",
      whenToUse: "Use when the x-axis implies time or ordered sequence.",
    },
    {
      id: "chart.bar",
      label: "Bar Chart",
      kind: "chart",
      importance: "high",
      description: "Best for category comparison, ranked lists, and before/after buckets.",
      whenToUse: "Use when values are discrete categories or cohorts.",
    },
    {
      id: "chart.doughnut",
      label: "Doughnut Chart",
      kind: "chart",
      importance: "medium",
      description: "Best for share-of-total, status mix, or channel allocation.",
      whenToUse: "Use with a small number of categories that add up to a meaningful whole.",
    },
    {
      id: "chart.radar",
      label: "Radar Chart",
      kind: "chart",
      importance: "medium",
      description: "Best for multi-factor scoring, capability comparison, or readiness profiles.",
      whenToUse: "Use when every label is a comparable dimension on the same scale.",
    },
    {
      id: "block.table",
      label: "Table",
      kind: "component",
      importance: "critical",
      description: "Detailed records with sortable columns, row actions, and optional expanded rows.",
      whenToUse: "Use when the user needs inspection, export-like detail, or per-row actions.",
      outputs: ["Columns", "rows", "row actions", "expanded row blocks"],
    },
    {
      id: "block.form",
      label: "Form",
      kind: "component",
      importance: "high",
      description: "Collects credentials, filters, workflow settings, or run parameters.",
      whenToUse: "Use for setup, verification, report generation, or user-controlled dashboard refreshes.",
    },
    {
      id: "block.notice",
      label: "Notice",
      kind: "component",
      importance: "medium",
      description: "Inline state, warnings, policy notes, or next-step guidance with actions.",
      whenToUse: "Use for connection failures, locked layouts, missing data, and important workflow state.",
    },
    {
      id: "action.download-report",
      label: "Download Report",
      kind: "action",
      importance: "high",
      description: "Exports a human-readable dashboard summary for handoff and review.",
      whenToUse: "Use when the user asks for a report, summary, audit, or deliverable.",
    },
    {
      id: "action.download-dashboard",
      label: "Download Dashboard",
      kind: "action",
      importance: "high",
      description: "Exports the dashboard manifest so the current layout and data can be inspected or archived.",
      whenToUse: "Use when the user asks to save, move, archive, or debug a dashboard state.",
    },
    {
      id: "feature.layout-lock",
      label: "Layout Lock",
      kind: "feature",
      importance: "critical",
      description:
        "Project-scoped guard that prevents AI-driven layout changes unless the user unlocks or grants permission.",
      whenToUse: "Respect before changing dashboard structure, block order, or visual layout.",
    },
  ],
}

export function mergedDashboardReferences(input?: DashboardComponentIndex) {
  const references = [...(dashboardComponentIndex.references ?? []), ...(input?.references ?? [])]
  const seen = new Set<string>()
  return references.filter((reference) => {
    if (seen.has(reference.id)) return false
    seen.add(reference.id)
    return true
  })
}
